import React, { useCallback } from "react"
import { motion } from "framer-motion"
import CentsImg from "../../../../../assets/avia/sidebar/cents.png"
import { useTranslation } from "react-i18next"
import { PRICE_VARIABLES } from "../../utils/variables/search"

const PriceFilter = ({ min, max, currency, onChange }) => {
  const { t } = useTranslation()

  const handleLeftChange = useCallback(
    (e) => {
      const newLeft = Math.min(Number(e.target.value), max - 1)
      onChange({ key: "price.min", value: newLeft, overwrite: true })
    },
    [min, max, onChange]
  )

  const handleRightChange = useCallback(
    (e) => {
      const newRight = Math.max(Number(e.target.value), min + 1)
      onChange({ key: "price.max", value: newRight, overwrite: true })
    },
    [max, min, onChange]
  )

  const leftPercent =
    ((min - PRICE_VARIABLES.DEFAULT_MIN) /
      (PRICE_VARIABLES.DEFAULT_MAX - PRICE_VARIABLES.DEFAULT_MIN)) *
    100
  const rightPercent =
    ((max - PRICE_VARIABLES.DEFAULT_MIN) /
      (PRICE_VARIABLES.DEFAULT_MAX - PRICE_VARIABLES.DEFAULT_MIN)) *
    100

  return (
    <motion.div
      className="mb-6 flex flex-col gap-[19px] relative"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="">
        <span className="text-dark-gray font-medium text-lg">
          {t("admiralsite.filter_by_price")}
        </span>
      </div>
      <div className="px-4 flex flex-col gap-1">
        <div className="relative h-6 ">
          <div className="absolute top-2 left-0 right-0 h-1 bg-gray-300 rounded-full" />

          <motion.div
            className="absolute top-2 h-1 bg-red-500 rounded-full"
            animate={{
              left: `${leftPercent}%`,
              right: `${100 - rightPercent}%`,
            }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          />

          <motion.div
            className="absolute -top-1 w-5 h-5 bg-white rounded-full shadow flex items-center justify-center cursor-pointer z-10"
            animate={{ left: `calc(${leftPercent}% - 10px)` }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            whileHover={{ scale: 1.2 }}
            whileTap={{ scale: 0.95 }}
            onMouseDown={(e) => {
              e.preventDefault()
              const startX = e.clientX
              const startValue = min
              const container = e.currentTarget.parentElement
              const rect = container.getBoundingClientRect()

              const handleMouseMove = (moveEvent) => {
                const deltaX = moveEvent.clientX - startX
                const deltaPercent = (deltaX / rect.width) * 100
                const valueRange = PRICE_VARIABLES.DEFAULT_MAX - PRICE_VARIABLES.DEFAULT_MIN
                const deltaValue = Math.round((deltaPercent / 100) * valueRange)
                const newValue = Math.max(
                  PRICE_VARIABLES.DEFAULT_MIN,
                  Math.min(max - 1, startValue + deltaValue)
                )

                handleLeftChange({ target: { value: newValue } })
              }

              const handleMouseUp = () => {
                document.removeEventListener("mousemove", handleMouseMove)
                document.removeEventListener("mouseup", handleMouseUp)
              }

              document.addEventListener("mousemove", handleMouseMove)
              document.addEventListener("mouseup", handleMouseUp)
            }}
          >
            <img src={CentsImg} alt="cents" className="w-3 h-3" />
          </motion.div>

          <motion.div
            className="absolute -top-1 w-5 h-5 bg-white rounded-full shadow flex items-center justify-center cursor-pointer z-10"
            animate={{ left: `calc(${rightPercent}% - 10px)` }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            whileHover={{ scale: 1.2 }}
            whileTap={{ scale: 0.95 }}
            onMouseDown={(e) => {
              e.preventDefault()
              const startX = e.clientX
              const startValue = max
              const container = e.currentTarget.parentElement
              const rect = container.getBoundingClientRect()

              const handleMouseMove = (moveEvent) => {
                const deltaX = moveEvent.clientX - startX
                const deltaPercent = (deltaX / rect.width) * 100
                const valueRange = PRICE_VARIABLES.DEFAULT_MAX - PRICE_VARIABLES.DEFAULT_MIN
                const deltaValue = Math.round((deltaPercent / 100) * valueRange)
                const newValue = Math.max(
                  min + 1,
                  Math.min(PRICE_VARIABLES.DEFAULT_MAX, startValue + deltaValue)
                )

                handleRightChange({ target: { value: newValue } })
              }

              const handleMouseUp = () => {
                document.removeEventListener("mousemove", handleMouseMove)
                document.removeEventListener("mouseup", handleMouseUp)
              }

              document.addEventListener("mousemove", handleMouseMove)
              document.addEventListener("mouseup", handleMouseUp)
            }}
          >
            <img src={CentsImg} alt="cents" className="w-3 h-3" />
          </motion.div>

          <input
            type="range"
            min={PRICE_VARIABLES.DEFAULT_MIN}
            max={PRICE_VARIABLES.DEFAULT_MAX}
            value={min}
            onChange={handleLeftChange}
            className="absolute top-0 left-0 w-full h-6 bg-transparent appearance-none cursor-pointer pointer-events-auto"
            style={{
              zIndex: min > max - (max - min) / 2 ? 5 : 3,
              opacity: 0,
            }}
          />

          <input
            type="range"
            min={PRICE_VARIABLES.DEFAULT_MIN}
            max={PRICE_VARIABLES.DEFAULT_MAX}
            value={max}
            onChange={handleRightChange}
            className="absolute top-0 left-0 w-full h-6 bg-transparent appearance-none cursor-pointer pointer-events-auto"
            style={{
              zIndex: min > max - (max - min) / 2 ? 3 : 5,
              opacity: 0,
            }}
          />
        </div>
        <div className="flex justify-between ">
          <CounterRow value={min} currency={currency} />
          <CounterRow value={max} currency={currency} />
        </div>
      </div>
      <div className="absolute -bottom-3 left-0 right-0 border-b  border-light-gray/25"></div>
    </motion.div>
  )
}

export default PriceFilter

const CounterRow = ({ value, currency }) => (
  <motion.span
    className="text-dark-gray text-[13px] bg-light-gray/15 px-1 rounded-md"
    key={value}
    initial={{ scale: 1 }}
    animate={{ scale: [1, 1.1, 1] }}
    transition={{ duration: 0.2 }}
  >
    {value}
    {currency.symbol}
  </motion.span>
)
